import { useRef, useState } from 'react';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';

interface OpenProjectInDAWButtonProps {
  onOpen: () => void | Promise<void>;
  label: string;
  disabledReason?: string;
  tabIndex?: number;
}

/** The same launch control is used in project details and the library. */
export function OpenProjectInDAWButton({
  onOpen,
  label,
  disabledReason,
  tabIndex,
}: OpenProjectInDAWButtonProps) {
  const pending = useRef(false);
  const [loading, setLoading] = useState(false);
  const launch = async () => {
    if (pending.current || disabledReason) return;
    pending.current = true;
    setLoading(true);
    try {
      await onOpen();
    } finally {
      pending.current = false;
      setLoading(false);
    }
  };

  return (
    <Tooltip title={disabledReason || (loading ? 'Opening project…' : label)}>
      <span
        style={{ display: 'inline-flex' }}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
      >
        <IconButton
          aria-label={label}
          aria-busy={loading}
          disabled={Boolean(disabledReason) || loading}
          tabIndex={tabIndex}
          size="small"
          color="primary"
          onClick={() => void launch()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              // Preserve native button activation without the grid handling it.
              event.stopPropagation();
            }
          }}
          sx={{ width: 32, height: 32, flexShrink: 0 }}
        >
          {loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <PlayArrow fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}
